import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import {
  Button,
  ButtonColorType,
  ButtonSizeType,
  ButtonVariantType
} from "@website/ui/button";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import {
  CheckboxFilter,
  Input,
  RadioFilter,
  RangeFilter,
  SwitchFilter
} from "@website/widget/filter/filter-components";
import { FilterMap } from "@website/widget/filter/filter-map";
import {
  useCityFilterState,
  useFilterLayoutEffect,
  useFilterTracking,
  useFilters
} from "@website/widget/filter/filter.hook";
import type {
  FiltersSchemaType,
  MapFilterProperties
} from "@website/widget/filter/filter.types";
import styles from "@website/widget/filter/index.module.scss";
import { useHandleScroll } from "@website/widget/filter/scroll.hook";
import { Children, Fragment, memo, useRef } from "react";

interface ResetButtonProperties {
  size: ButtonSizeType;
  color: ButtonColorType;
  variant: ButtonVariantType;
}

interface FilterProperties {
  schema: FiltersSchemaType;
  title: string;
  loading?: boolean;
  filtersActionTitle: string;
  resetButtonProps: ResetButtonProperties;
  className?: string;
  filtersClassName?: string;
  mapFilterProps?: MapFilterProperties;
  scroll?: boolean;
  trackingCallback?({ removed }: { removed: object }): void;
  dontSyncScroll?: boolean;
}

const Component = (properties: FilterProperties) => {
  const {
    schema,
    loading,
    title,
    filtersActionTitle,
    resetButtonProps,
    className,
    filtersClassName,
    mapFilterProps,
    scroll,
    trackingCallback,
    dontSyncScroll
  } = properties;
  const containerReference = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewPort();
  const { formRef } = useFilterLayoutEffect({ dontSyncScroll });
  const { selectedFiltersCount, filterKeys, clearFilters } = useFilters({
    schema
  });
  const { onCloseFilters, visible } = useCityFilterState();

  useHandleScroll({ scroll, containerReference, filterKeys });
  useFilterTracking({ callback: trackingCallback, schema });

  const filtersSchemaEntries = Object.entries(schema);
  const children = filtersSchemaEntries.map(([name, schema]) => {
    switch (schema.type) {
      case "range": {
        return <RangeFilter key={name} {...schema} />;
      }
      case "input": {
        return <Input key={name} name={name} {...schema} />;
      }
      case "switch": {
        return <SwitchFilter key={name} name={name} {...schema} />;
      }
      case "radio": {
        return <RadioFilter key={name} name={name} {...schema} />;
      }
      case "checkbox": {
        return <CheckboxFilter key={name} name={name} {...schema} />;
      }
      case "custom": {
        return <Fragment key={name}>{schema.component}</Fragment>;
      }
      default: {
        throw { error: "FILTER NOT FOUND" };
      }
    }
  });

  return (
    <aside
      className={`${styles["panel-container"]} ${
        visible ? styles["visible"] : ""
      } ${className ?? ""}`}
      role={visible && isMobile ? "dialog" : undefined}
      ref={containerReference}
    >
      {mapFilterProps && (
        <FilterMap
          label={mapFilterProps.label}
          onClick={mapFilterProps.onClick}
        />
      )}
      <div className={`${styles["filters"]} ${filtersClassName ?? ""}`}>
        <div className={styles["filters-header-box"]}>
          <div className={styles["filters-title-container"]}>
            <Button
              className={styles["close-button"]}
              color="neutral"
              onClick={onCloseFilters}
              variant="text"
            >
              <MaterialIcon
                className="text-on-surface-medium-emphasis text-2xl"
                name="arrow_forward"
              />
            </Button>
            <span className={styles["filters-header-title"]}>{title}</span>
          </div>
          <Button
            {...resetButtonProps}
            StartIcon={
              Number(selectedFiltersCount) ? (
                <MaterialIcon name="delete_outline" />
              ) : null
            }
            className="h-16"
            hidden={!selectedFiltersCount}
            onClick={clearFilters}
          >
            {selectedFiltersCount
              ? `حذف فیلترها (${numberConvert(selectedFiltersCount, {
                  locale: "fa"
                })})`
              : "حذف فیلترها"}
          </Button>
        </div>
        <div className={styles["filters-form-container"]} ref={formRef}>
          {Children.map(children, (child, index) => (
            <Fragment key={child?.key ?? index}>
              {child}
              {Children.count(children) - 1 !== index && (
                <div className={styles["divider"]} />
              )}
            </Fragment>
          ))}
        </div>
        <div className={styles["filters-footer-container"]}>
          <Button
            color="secondary"
            fullWidth
            loading={loading}
            onClick={onCloseFilters}
            size="large"
          >
            {filtersActionTitle}
          </Button>
        </div>
      </div>
    </aside>
  );
};

export const Filters = memo(Component, deepCompare);
